import React, { useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import { useLocation } from "react-router";

import Loader from "@components/shared/Loader/Loader";
import { PAYMENT_STATUS } from "@constants/Bookings";
import { CONSTANTS } from "@constants/index";
import { PaymentDetailsResponse } from "@models/components/booking.model";
import { BookedDataType, UniqueClubsData } from "@models/components/user.model";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import commonUtils from "@utils/common.utils";
import { compareDates, getTabWithBookedDate } from "@utils/user.util";

import CancelBooking from "./CancelBooking/CancelBooking";
import ReservationCard from "./ReservationCard/ReservationCard";
import ReservationDetails from "./ReservationDetails/ReservationDetails";
const ReservationsComponent = () => {
  const { RESERVATION_TABS } = CONSTANTS;
  const location = useLocation();
  const queryParameters = new URLSearchParams(location.search);
  const paymentId = queryParameters.get("paymentId");
  const bookingDate = queryParameters.get("date");
  const period = getTabWithBookedDate(bookingDate || "");
  const bookingId: string = queryParameters.get("bookingId") ?? "";
  const getTab = () => {
    if (period) return period;
    else return RESERVATION_TABS.TODAY;
  };
  const [selectedClub, setSelectedClub] = useState<string>("0");
  const [filteredItems, setFilteredItems] = useState<Array<BookedDataType>>([]);
  const [selectedTab, setSelectedTab] = useState<string>(
    getTab() || RESERVATION_TABS.TODAY
  );
  const [bookingsData, setBookingsData] = useState<Array<BookedDataType>>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [cancelModal, toggleCancelModal] = useState(false);

  const { t } = useTranslation();

  const reservationTabs = [
    RESERVATION_TABS.PAST,
    RESERVATION_TABS.TODAY,
    RESERVATION_TABS.FUTURE,
    RESERVATION_TABS.CANCELLED,
  ];

  useEffect(() => {
    getUserBookingsFn();
  }, []);

  useEffect(() => {
    setFilteredData();
  }, [selectedTab, bookingsData, selectedClub]);

  useEffect(() => {
    if (paymentId) {
      getPaymentStatus(paymentId);
    }
  }, [paymentId]);

  useEffect(() => {
    if (bookingDate) {
      clearUrlParams("date");
    }
  }, [bookingDate]);

  useEffect(() => {
    if (bookingId) {
      setShowDetailsModal(true);
      clearUrlParams("bookingId");
    }
  }, [bookingId]);

  const clearUrlParams = (key: string) => {
    queryParameters.delete(key);

    let newURL = window.location.pathname;
    if (queryParameters?.size) newURL += "?" + queryParameters?.toString();

    window.history.replaceState({}, "", newURL);
  };

  const getPaymentMessage = (data: PaymentDetailsResponse) => {
    if (data?.bookingStatus)
      commonUtils.toastService(
        t("paymentSuccess"),
        CONSTANTS.TOAST_TYPE.SUCCESS
      );
    else if (data?.paymentStatus === PAYMENT_STATUS.FAILED)
      commonUtils.toastService(t("paymentFail"), CONSTANTS.TOAST_TYPE.ERROR);
    else if (
      data?.paymentStatus === PAYMENT_STATUS.SUCCESS &&
      data?.partialPayment
    )
      commonUtils.toastService(t("partialPayment"), CONSTANTS.TOAST_TYPE.ERROR);
    else if (data?.paymentStatus === PAYMENT_STATUS.SUCCESS)
      commonUtils.toastService(t("paymentTimeout"), CONSTANTS.TOAST_TYPE.ERROR);
  };

  const getPaymentStatus = async (paymentId: string) => {
    const data = await API_SERVICES.BOOKING.PAYMENT_STATUS(paymentId);
    const responseData = handleResponse(data);
    clearUrlParams("paymentId");
    if (responseData?.ok) {
      getPaymentMessage(responseData?.data);
    }
  };

  const clubListRender = () => {
    const uniqueClubs: UniqueClubsData[] = [];
    const seenClubIds = new Set();

    bookingsData.forEach(item => {
      const clubId = item.club.id;
      if (!seenClubIds.has(clubId)) {
        seenClubIds.add(clubId);

        uniqueClubs.push({ ...item.club });
      }
    });

    return uniqueClubs.map(club => (
      <option key={club.id} value={club.id}>
        {applyLocalization(club.title)}
      </option>
    ));
  };

  const setFilteredData = () => {
    if (bookingsData?.length) {
      const filtered = bookingsData?.filter(item => {
        // Check if the selected club matches
        const clubMatch = selectedClub === "0" || selectedClub === item.club.id;

        // Filter out canceled bookings, and also filter based on selected tab
        const statusMatch =
          selectedTab === RESERVATION_TABS.CANCELLED
            ? item?.bookingCanceled
            : !item?.bookingCanceled &&
              compareDates(item?.startDate, selectedTab);
        return clubMatch && statusMatch;
      });

      // Sorting logic (same as before)
      const sortedFilteredItems = filtered.sort((a, b) => {
        const datetimeA = new Date(`${a.date}T${a.startTime}`);
        const datetimeB = new Date(`${b.date}T${b.startTime}`);

        if (
          selectedTab === RESERVATION_TABS.PAST ||
          selectedTab === RESERVATION_TABS.CANCELLED
        ) {
          return datetimeB.getTime() - datetimeA.getTime();
        } else if (
          selectedTab === RESERVATION_TABS.FUTURE ||
          selectedTab === RESERVATION_TABS.TODAY
        ) {
          return datetimeA.getTime() - datetimeB.getTime();
        }
        return 0;
      });

      setFilteredItems(sortedFilteredItems); // Set the filtered and sorted data
    }
  };

  const handleClubChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedClub(event.target.value); // Update the selected club
  };

  const getUserBookingsFn = async () => {
    setIsFetching(true);
    const data = await API_SERVICES.USER.GET_USER_BOOKINGS();
    const responseData = handleResponse(data);
    if (responseData?.ok) {
      setBookingsData(responseData.data);
      setIsFetching(false);
    } else setIsFetching(false);
  };

  const renderTabs = () => {
    return reservationTabs.map((tab: string) => (
      <li key={tab} className={selectedTab === tab ? "fg-loc-active" : ""}>
        <button
          onClick={() => setSelectedTab(tab)}
          data-testid="reservation-period-tabs"
        >
          {t(tab)}
        </button>
      </li>
    ));
  };

  return (
    <div
      className="tab-pane fade show active"
      id="pills-reservation"
      role="tabpanel"
      aria-labelledby="booking-tab"
      data-testid="reservations-test"
    >
      <div className="fg-tab-content-wrap">
        <div className="fg-location-block">
          <div className="container">
            <select
              className="fg-location-club-select fg-unit-select-custom"
              onChange={handleClubChange}
              value={selectedClub}
            >
              <option value="0">{t("allClubs")}</option>
              {clubListRender()}
            </select>
            <ul className="fg-loc-scroll">{renderTabs()}</ul>
          </div>
        </div>
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <div className="fg-reservations-card-wrap">
                {isFetching && <Loader isLoading={isFetching} />}

                {filteredItems?.length > 0 ? (
                  filteredItems?.map((item: BookedDataType) => (
                    <ReservationCard
                      data={item}
                      getUserBookings={getUserBookingsFn}
                      key={item?.bookingId}
                    />
                  ))
                ) : (
                  <div
                    data-testid="no-reservation"
                    className="fg-no-reservation"
                  >
                    {t("noBooking")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <CancelBooking
        bookingId={bookingId}
        cancelModal={cancelModal}
        getUserBookings={getUserBookingsFn}
        toggleCancelModal={() => toggleCancelModal(false)}
      />
      {showDetailsModal && (
        <ReservationDetails
          bookingData={{
            bookingId: bookingId,
          }}
          isOpen={showDetailsModal}
          toggleCancelModal={() => {
            toggleCancelModal(true);
            setShowDetailsModal(false);
          }}
          toggleOpen={() => setShowDetailsModal(false)}
        />
      )}
    </div>
  );
};

const Reservations = withTranslation()(ReservationsComponent);

export default Reservations;
